'use client';
import { FC, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { searchParams } from '@/utils/helper';

import { Svg, Text } from '@/components';
import { ArrowIcon, CloseIcon } from '@/icons';
import RecipeCard from '@/components/Card/Recipe';
import ProductCard from '@/components/ProductCardWizzy';
import {
  getThumbnail,
  getValueFromAttributes,
  googleDataLayerEventPush,
  pushGA4Event,
} from '@/utils/helper';
import Carousel from '@/layouts/Carousel';
import WorkshopsCard from '@/components/WorkshopsCard';
import { MENU_LINKS } from '@/constants';
import { useHeaderContext } from '@/context/headerContext';
import Image from 'next/image';
import ArticleCard from '@/features/Stories/components/ArticleCard';
import { useCleverTap } from '@/context/clevertapContext';
import Drawer from '@/layouts/Drawer/Drawer';

interface ExploreMorePropTypes {
  fontSize?: 'sm' | 'lg';
  type?: string;
}

interface PreSearchPropTypes {
  heading: string;
  array: any[];
  handleSearch: (value?: string, index?: number) => void;
  type: 'autoComplete' | 'popularSearches';
}

interface ProductViewPropTypes {
  title: string;
  products: {
    sku: string;
    name: string;
    price: number;
    custom_attributes?: any[];
  }[];
  searchResponseId: string | null;
  onClose: (value?: boolean) => void;
  handleSearch: (value?: string) => void;
  type?: string;
}

const SETTINGS = {
  arrows: true,
  autoplay: false,
  infinite: false,
  slidesToShow: 3,
  progress: true,
  responsive: [
    {
      breakpoint: 780,
      settings: {
        slidesToShow: 2.2,
        infinite: false,
      },
    },
    {
      breakpoint: 580,
      settings: {
        slidesToShow: 1.8,
        arrows: false,
        infinite: false,
      },
    },
    {
      breakpoint: 425,
      settings: {
        slidesToShow: 1.2,
        arrows: false,
        infinite: false,
      },
    },
  ],
};

const ExploreMore: FC<ExploreMorePropTypes> = ({ fontSize = 'sm', type }) => {
  const exploreClass =
    fontSize === 'lg'
      ? 'flex cursor-pointer items-center gap-x-3 text-sm md:text-lg font-bold text-fig underline px-0'
      : 'flex cursor-pointer items-center gap-x-3 text-sm font-bold text-fig underline px-0';
  return (
    <div className={exploreClass}>
      {type === 'autocomplete' ? 'View all Products' : 'Explore More'}
      <Svg
        fill='none'
        width={'1.125rem'}
        height={'1.125rem'}
        className='mb-[0.3rem] -rotate-90 stroke-fig'>
        <ArrowIcon />
      </Svg>
    </div>
  );
};

const PreSearch: FC<PreSearchPropTypes> = ({
  heading,
  array,
  handleSearch,
  type,
}) => {
  return (
    <div>
      <div className='flex flex-wrap md:hidden gap-3 pr-10 mt-[-27px]'>
        {array?.slice(0, 6).map((item, index) => {
          return type === 'autoComplete' ? (
            <button
              key={index}
              onClick={() => handleSearch(item?.value, index)}>
              <div className='cursor-pointer border border-fig px-2 py-1 rounded-lg'>
                <p className='text-ellipsis text-left text-fig text-xs font-semibold'>
                  {item?.value}
                </p>
              </div>
            </button>
          ) : (
            <button key={index} onClick={() => handleSearch(item, index)}>
              <div className='cursor-pointer border border-fig px-2 py-1 rounded-lg'>
                <p className='text-ellipsis text-left text-fig text-xs font-semibold'>
                  {item}
                </p>
              </div>
            </button>
          );
        })}
      </div>
      {array && array.length > 0 && (
        <div className='mb-4 w-full md:flex hidden'>
          <Text className='px-0 text-lg font-semibold'>{heading}</Text>
        </div>
      )}
      <ul className='lg:flex w-full flex-col items-start gap-y-4 text-start hidden'>
        {array?.map((item, index) => {
          return type === 'autoComplete' ? (
            <button
              key={index}
              onClick={() => handleSearch(item?.value, index + 1)}>
              <li className='cursor-pointer hover:underline'>
                <p className='text-ellipsis text-left'>{item?.value}</p>
              </li>
            </button>
          ) : (
            <button key={index} onClick={() => handleSearch(item, index + 1)}>
              <li className='cursor-pointer hover:underline'>
                <p className='text-ellipsis text-left'>{item}</p>
              </li>
            </button>
          );
        })}
      </ul>
    </div>
  );
};

const ProductView: FC<ProductViewPropTypes> = ({
  title,
  products,
  searchResponseId,
  onClose,
  handleSearch,
  type,
}) => {
  useEffect(() => {
    if (products && products.length > 0) {
      googleDataLayerEventPush({
        eventName: 'view_item_list',
        eventParameters: {
          item_list_id:
            title === 'Trending Products'
              ? 'ARGOID_HOME_TRENDING'
              : 'ARGOID_SEARCH',
          item_list_name:
            title === 'Trending Products'
              ? 'Trending Products (Home Page)'
              : 'Search (Auto-Complete)',
          items: products,
        },
      });
    }
  }, [products.length]);

  if (!products || products.length === 0) {
    return (
      <div className='relative w-full'>
        <div className='mb-6 flex w-full items-center justify-between gap-x-4'>
          <Text className='px-0 text-lg font-semibold'>{title}</Text>
        </div>
        <Text className='px-0 text-lg'>No {title} as of now</Text>
      </div>
    );
  }

  return (
    <div className='relative w-full'>
      <div className='mb-6 flex w-full items-center justify-between'>
        <Text className='px-0 text-lg font-semibold'>{title}</Text>
        <button onClick={() => handleSearch()}>
          <ExploreMore fontSize='lg' type={type} />
        </button>
      </div>

      <div className='w-full items-center justify-center grid grid-cols-2 gap-1 md:hidden pr-4 gap-x-4 gap-y-6'>
        {products?.map((item: any, index: number) => {
          if (title === 'Recipes') {
            return (
              <div
                className='w-full px-1'
                key={index}
                onClick={() => onClose()}>
                <RecipeCard
                  {...item}
                  heading={item?.name}
                  src={
                    getThumbnail({
                      custom_attributes: item?.custom_attributes,
                    }) ?? ''
                  }
                  duration={
                    getValueFromAttributes({
                      custom_attributes: item?.custom_attributes,
                      code: 'time_to_cooking',
                    })?.value
                  }
                  author={
                    getValueFromAttributes({
                      custom_attributes: item?.custom_attributes,
                      code: 'author',
                    })?.value
                  }
                  classNames={{
                    root: 'mx-0',
                  }}
                  searchResult='AUTO_COMPLETE'
                  plpCard={true}
                />
              </div>
            );
          } else if (title === 'Stories') {
            return (
              <div
                className='basis-1/2 px-1 md:basis-1/3'
                key={item?.sku}
                onClick={() => onClose()}>
                <ArticleCard article={item} />
              </div>
            );
          } else {
            return (
              <div
                className='basis-1/2 px-1 md:basis-1/3'
                key={index}
                onClick={() => onClose()}>
                <ProductCard
                  plpCard={true}
                  item={item}
                  searchResult={
                    title !== 'Trending Products' ? 'AUTO_COMPLETE' : undefined
                  }
                  wizzyEventData={{
                    name: 'Search Results Clicked',
                    wizzySource: 'SEARCH_RESULTS',
                    wizzySearchResponseId: searchResponseId,
                  }}
                  index={index}
                />
              </div>
            );
          }
        })}
      </div>

      <div className='w-full md:block hidden'>
        {products.length > 2 ? (
          <Carousel settings={SETTINGS}>
            {products?.map((item: any, index: number) => {
              return (
                <div className='px-2' key={index} onClick={() => onClose()}>
                  {title === 'Recipes' ? (
                    <RecipeCard
                      {...item}
                      heading={item?.name}
                      src={
                        getThumbnail({
                          custom_attributes: item?.custom_attributes,
                        }) ?? ''
                      }
                      duration={
                        getValueFromAttributes({
                          custom_attributes: item?.custom_attributes,
                          code: 'time_to_cooking',
                        })?.value
                      }
                      author={
                        getValueFromAttributes({
                          custom_attributes: item?.custom_attributes,
                          code: 'author',
                        })?.value
                      }
                      searchResult='AUTO_COMPLETE'
                    />
                  ) : title === 'Stories' ? (
                    <ArticleCard article={item} />
                  ) : (
                    <ProductCard
                      plpCard={true}
                      index={index}
                      item={item}
                      eventMetadata={{
                        sectionName:
                          title !== 'Trending Products'
                            ? 'AutoComplete Search Result'
                            : 'Trending Products',
                        rank: index + 1,
                      }}
                      dataLayerSource={
                        title === 'Trending Products'
                          ? 'ARGOID_HOME_TRENDING'
                          : 'ARGOID_SEARCH_AUTOCOMPLETE'
                      }
                      searchResult={
                        title !== 'Trending Products'
                          ? 'AUTO_COMPLETE'
                          : undefined
                      }
                      wizzyEventData={{
                        name:
                          title === 'Trending Products'
                            ? 'Product Viewed'
                            : 'Search Results Clicked',
                        wizzySource:
                          title === 'Trending Products'
                            ? 'FEATURED_VIEW'
                            : 'SEARCH_RESULTS',
                        wizzySearchResponseId: searchResponseId,
                      }}
                      classNames={{ root: 'mx-auto' }}
                    />
                  )}
                </div>
              );
            })}
          </Carousel>
        ) : (
          <div className='oveflow-x-auto flex w-full items-start justify-start'>
            {products?.map((item: any, index: number) => {
              if (title === 'Recipes') {
                return (
                  <div
                    className='w-full px-2'
                    key={index}
                    onClick={() => onClose()}>
                    <RecipeCard
                      {...item}
                      heading={item?.name}
                      src={
                        getThumbnail({
                          custom_attributes: item?.custom_attributes,
                        }) ?? ''
                      }
                      duration={
                        getValueFromAttributes({
                          custom_attributes: item?.custom_attributes,
                          code: 'time_to_cooking',
                        })?.value
                      }
                      author={
                        getValueFromAttributes({
                          custom_attributes: item?.custom_attributes,
                          code: 'author',
                        })?.value
                      }
                      classNames={{
                        root: 'mx-0',
                      }}
                      searchResult='AUTO_COMPLETE'
                    />
                  </div>
                );
              } else if (title === 'Stories') {
                return (
                  <div
                    className='basis-1/2 px-2 md:basis-1/3'
                    key={item?.sku}
                    onClick={() => onClose()}>
                    <ArticleCard article={item} />
                  </div>
                );
              } else {
                return (
                  <div
                    className='basis-1/2 px-2 md:basis-1/3'
                    key={index}
                    onClick={() => onClose()}>
                    <ProductCard
                      plpCard={true}
                      item={item}
                      searchResult={
                        title !== 'Trending Products'
                          ? 'AUTO_COMPLETE'
                          : undefined
                      }
                      wizzyEventData={{
                        name: 'Search Results Clicked',
                        wizzySource: 'SEARCH_RESULTS',
                        wizzySearchResponseId: searchResponseId,
                      }}
                      index={index}
                    />
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const WorkshopsView: FC<ProductViewPropTypes> = ({
  title,
  products,
  onClose,
}) => {
  const router = useRouter();
  return (
    <div className='relative w-full'>
      <div className='mb-6 flex w-full items-center justify-between'>
        <Text className='px-0 text-lg font-semibold'>{title}</Text>
        <button
          onClick={() => {
            onClose();
            router.push('/kitchen-studio?tag=events-list');
          }}>
          <ExploreMore fontSize='lg' />
        </button>
      </div>

      <div className='block w-full'>
        {products.length > 2 ? (
          <Carousel settings={SETTINGS}>
            {products?.map((item: any, index: number) => {
              return (
                <div className='px-2' key={index} onClick={() => onClose()}>
                  <WorkshopsCard item={item} />
                </div>
              );
            })}
          </Carousel>
        ) : (
          <div className='overflow-x-auto flex w-full items-start justify-start md:gap-6'>
            {products?.map((item: any, index: number) => {
              return (
                <div className='px-2' key={index} onClick={() => onClose()}>
                  <WorkshopsCard item={item} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const SearchDropdown: FC = () => {
  const {
    query: searchQuery = '',
    trendingProducts,
    popularSearches,
    upcomingWorkshops,
    autoCompleteProducts: products,
    autoCompleteRecipes: recipes,
    autoCompleteArticles: stories,
    isSearchDrawerOpen,
    toggleIsSearchDrawerOpen,
    loading,
    showNoProductsFound,
    autoCompleteResponseId,
    searchBarRef,
    setQuery,
    searchAutoCompleteCategories,
  } = useHeaderContext();
  const router = useRouter();
  const { pushEvent } = useCleverTap();
  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);
      return params.toString();
    },
    [searchParams]
  );
  function handleSearch(query?: any, index?: number) {
    toggleIsSearchDrawerOpen(false);
    setQuery(query ?? searchQuery);
    pushEvent({
      event: 'Popular Search Clicked',
      data: {
        Timestamp: new Date(),
        Keyword: query,
        Rank: index ? index : null,
        Query: searchQuery === '' ? null : searchQuery,
      },
    });
    if (!query && !searchQuery) {
      router.push(MENU_LINKS.SHOP);
      return;
    }

    // GA4 Discovery: search_query
    pushGA4Event('search_query', { search_term: query ?? searchQuery });
    router.push(
      '/search-results' + '?' + createQueryString('query', query ?? searchQuery)
    );
  }

  const parentDefaultClass =
    'flex max-lg:flex-col bg-white w-full overflow-y-auto py-4 pt-8 md:pt-4 lg:px-18 max-lg:pl-4 overflow-x-hidden relative items-start justify-center';

  const closeCallback = useCallback(() => {
    if (isSearchDrawerOpen) {
      toggleIsSearchDrawerOpen(false);
      setQuery('');
      if (searchBarRef) {
        searchBarRef?.current?.blur();
      }
    }
  }, [searchBarRef, setQuery, toggleIsSearchDrawerOpen, isSearchDrawerOpen]);

  useEffect(() => {
    if (isSearchDrawerOpen) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      // Prevent background scrolling
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
    } else {
      // Restore the scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }

    // Cleanup function
    return () => {
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
    };
  }, [isSearchDrawerOpen]);

  return (
    <Drawer isDrawerOpen={isSearchDrawerOpen} closeCallback={closeCallback}>
      {!loading ? (
        <div className={parentDefaultClass}>
          {!searchQuery &&
            upcomingWorkshops &&
            upcomingWorkshops.length > 0 && (
              <div className='block w-full py-8 md:hidden'>
                <WorkshopsView
                  title='Upcoming Workshops'
                  products={upcomingWorkshops}
                  onClose={() => toggleIsSearchDrawerOpen(false)}
                  handleSearch={handleSearch}
                  searchResponseId={autoCompleteResponseId}
                />
              </div>
            )}
          <button
            type='button'
            onClick={() => closeCallback()}
            className='absolute right-4 top-2 rounded-full bg-transparent lg:top-4'
            aria-label='close'
            autoFocus={false}
            title='Close'>
            <Svg>
              <CloseIcon />
            </Svg>
          </button>
          <div className='flex flex-col md:w-[25%] lg:pr-8'>
            {showNoProductsFound && searchQuery && searchQuery !== '' && (
              <div className='text-sm font-semibold block md:hidden mt-[-27px]'>
                Could not find any results for "{searchQuery}"
              </div>
            )}
            {((popularSearches && popularSearches.length > 0) ||
              (searchAutoCompleteCategories &&
                searchAutoCompleteCategories.length > 0)) && (
              <PreSearch
                heading='Popular Searches'
                array={
                  searchQuery.length > 2
                    ? searchAutoCompleteCategories
                    : popularSearches
                }
                handleSearch={handleSearch}
                type={
                  searchQuery.length > 2 ? 'autoComplete' : 'popularSearches'
                }
              />
            )}
          </div>
          <div className='w-full flex-col items-center justify-center gap-y-11 pb-16 lg:flex lg:w-[75%] 2xl:max-w-7xl md:mt-0 mt-2'>
            {showNoProductsFound && searchQuery && searchQuery?.length > 2 && (
              <div className='md:flex w-full flex-col items-center justify-center gap-y-4 hidden'>
                <Text className='px-0 text-2xl font-semibold'>
                  Could not find any results for "{searchQuery}"
                </Text>
              </div>
            )}
            {(!searchQuery || searchQuery?.length < 3 || showNoProductsFound) &&
              trendingProducts?.length > 0 && (
                <div className='w-full md:mt-0 mt-3'>
                  <ProductView
                    title='Trending Products'
                    products={trendingProducts}
                    onClose={() => toggleIsSearchDrawerOpen(false)}
                    handleSearch={handleSearch}
                    searchResponseId={autoCompleteResponseId}
                  />
                </div>
              )}
            {searchQuery && products?.length > 0 && (
              <div className='w-full md:mt-0 mt-5'>
                <ProductView
                  title='Products'
                  type='autocomplete'
                  products={products}
                  onClose={() => toggleIsSearchDrawerOpen(false)}
                  handleSearch={handleSearch}
                  searchResponseId={autoCompleteResponseId}
                />
              </div>
            )}
            {searchQuery && recipes?.length > 0 && (
              <div className='w-full md:mt-0 mt-5'>
                <ProductView
                  title='Recipes'
                  products={recipes}
                  onClose={() => toggleIsSearchDrawerOpen(false)}
                  handleSearch={handleSearch}
                  searchResponseId={autoCompleteResponseId}
                />
              </div>
            )}
            {searchQuery && stories?.length > 0 && (
              <div className='w-full md:mt-0 mt-5'>
                <ProductView
                  title='Stories'
                  products={stories}
                  onClose={() => toggleIsSearchDrawerOpen(false)}
                  handleSearch={handleSearch}
                  searchResponseId={autoCompleteResponseId}
                />
              </div>
            )}
            {!searchQuery && upcomingWorkshops?.length > 0 && (
              <div className='hidden w-full pt-8 md:block md:mt-0 mt-5'>
                <WorkshopsView
                  title='Upcoming Workshops'
                  products={upcomingWorkshops}
                  onClose={() => toggleIsSearchDrawerOpen(false)}
                  handleSearch={handleSearch}
                  searchResponseId={autoCompleteResponseId}
                />
              </div>
            )}
            {searchQuery && products?.length > 0 && (
              <div className='flex items-center justify-center md:hidden mt-5'>
                <button onClick={() => handleSearch()}>
                  <ExploreMore fontSize='sm' type='autocomplete' />
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className='flex flex-col w-full h-[70vh] items-center justify-center sm:h-[75vh]'>
          <div className='relative mx-auto aspect-square h-40 w-40 md:h-80 md:w-[20rem] z-22 bg-white'>
            <Image
              src='/images/global/loading.gif'
              alt='Loading'
              title='Loading'
              className='object-contain'
              sizes='20vw'
              draggable={false}
              unoptimized
              fill
            />
          </div>
          <Text className='font-medium italic'>
            Writing Delicious Stories...
          </Text>
        </div>
      )}
    </Drawer>
  );
};

export default SearchDropdown;
